import { getInternalChatId, getTpoChatId } from 'app/shared/components/TeamChat/TeamChat.utils';
import _ from 'lodash';

import {
  LOAN_EDITOR_CHAT_CHANNEL_LABEL,
  LOAN_EDITOR_CHAT_DRAWER_DEFAULT_WIDTH,
  LOAN_EDITOR_CHAT_DRAWER_MAX_WIDTH,
  LOAN_EDITOR_CHAT_DRAWER_MIN_WIDTH,
  LoanEditorChatChannelKey,
  LoanEditorChatDrawerStorageKey,
} from './LoanEditorChatDrawer.constants';

export { getInternalChatId, getTpoChatId };

export const formatUnreadCountLabel = (count, maxDisplay) => {
  if (!count) {
    return '';
  }

  return count > maxDisplay ? `${maxDisplay}+` : String(count);
};

export const getTotalUnreadCount = (channels, unreadByChannelKey) =>
  (channels ?? []).reduce((sum, { key }) => sum + (unreadByChannelKey?.[key] ?? 0), 0);

export const clampDrawerWidth = value => {
  if (!Number.isFinite(value)) {
    return LOAN_EDITOR_CHAT_DRAWER_DEFAULT_WIDTH;
  }

  return Math.max(
    LOAN_EDITOR_CHAT_DRAWER_MIN_WIDTH,
    Math.min(LOAN_EDITOR_CHAT_DRAWER_MAX_WIDTH, Math.round(value)),
  );
};

export const readStoredOpenState = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(LoanEditorChatDrawerStorageKey.OPEN) === 'true';
};

export const writeStoredOpenState = value => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(LoanEditorChatDrawerStorageKey.OPEN, value ? 'true' : 'false');
};

export const readStoredWidth = () => {
  if (typeof window === 'undefined') {
    return LOAN_EDITOR_CHAT_DRAWER_DEFAULT_WIDTH;
  }

  const raw = window.localStorage.getItem(LoanEditorChatDrawerStorageKey.WIDTH);

  if (!raw) {
    return LOAN_EDITOR_CHAT_DRAWER_DEFAULT_WIDTH;
  }

  return clampDrawerWidth(Number.parseInt(raw, 10));
};

export const writeStoredWidth = value => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    LoanEditorChatDrawerStorageKey.WIDTH,
    String(clampDrawerWidth(value)),
  );
};

export const getChannelKeyForChatId = (chatId, { internalChatId, tpoChatId }) => {
  if (chatId && chatId === internalChatId) {
    return LoanEditorChatChannelKey.INTERNAL;
  }

  if (chatId && chatId === tpoChatId) {
    return LoanEditorChatChannelKey.TPO;
  }

  return null;
};

export const buildLoanEditorChatChannels = ({
  internalChatId,
  tpoChatId,
  isTpoChannelAvailable,
  isInternalChannelAvailable = true,
}) => {
  const channels = [];

  if (isInternalChannelAvailable && internalChatId) {
    channels.push({
      key: LoanEditorChatChannelKey.INTERNAL,
      chatId: internalChatId,
      label: LOAN_EDITOR_CHAT_CHANNEL_LABEL[LoanEditorChatChannelKey.INTERNAL],
    });
  }

  if (isTpoChannelAvailable && tpoChatId) {
    channels.push({
      key: LoanEditorChatChannelKey.TPO,
      chatId: tpoChatId,
      label: LOAN_EDITOR_CHAT_CHANNEL_LABEL[LoanEditorChatChannelKey.TPO],
    });
  }

  return channels;
};

// A sub-company (broker) chat member's name arrives from the Chats API as their raw id (DEV-4226
// — the server-side resolver that gives employees a real name needs Accounts /api/users, which a
// broker 403s on). Build a fallback {userId: name} map from data the loan editor already has
// client-side for this loan — the assigned loan-role users (LO/processor/etc.) and the viewer's
// own name — so the chat can resolve a human name instead of showing the GUID.
export const buildKnownMemberNamesByUserId = ({
  extendedLoanRoles,
  currentUserId,
  currentUserData,
}) => {
  const namesByUserId = {};

  (extendedLoanRoles ?? []).forEach(role => {
    const userId = role?.pendingChange?.newUserId || role?.userId;
    const fullName = _.trim(role?.fullName);

    if (userId && fullName) {
      namesByUserId[userId] = fullName;
    }
  });

  const currentUserFullName = _.trim(
    `${currentUserData?.firstName ?? ''} ${currentUserData?.lastName ?? ''}`,
  );

  if (currentUserId && currentUserFullName) {
    namesByUserId[currentUserId] = currentUserFullName;
  }

  return namesByUserId;
};

export const getChatInitializationErrorMessage = error => {
  const serverMessage = error?.response?.data?.error?.message;

  if (_.isString(serverMessage) && serverMessage.length > 0) {
    return serverMessage;
  }

  return "Unable to load this loan's chat. Please try again later.";
};
